import { useMemo, useState } from 'react'

/**
 * TileTable — a deliberately lightweight sortable table for dashboard tiles
 * (CC-49332). Tiles show a handful of rows at a glance, so they don't need the
 * full DataGrid (pagination, filters, column drag, persistence) — just
 * click-to-sort headers that behave EXACTLY like every other sortable surface
 * (CC-49333 consistency contract):
 *
 *   - first click sorts ASCENDING — including numeric/currency/date columns
 *     (CC-49378), second click descending, third clears (DataGrid's cycle)
 *   - ▲ / ▼ indicator, aria-sort on the <th>
 *
 *     The indicator's TREATMENT is part of that contract, not decoration on top
 *     of it: it used to read "in the accent color", which is why this dashboards
 *     file is in the 2026-08-26 report-chrome PR at all. B5 restyles the mark on
 *     both report grids to `text-rc-muted text-[10px] leading-none` — a quiet
 *     10px mark that sits beside the label instead of competing with it — so
 *     leaving TileTable on text-cb-accent-text would have broken the CC-49333
 *     consistency contract in the very file that writes it down. All three
 *     surfaces now carry the same treatment.
 *
 *     The GLYPHS are load-bearing and stay literal characters, never an SVG:
 *     six test files assert `textContent` contains them (TileTable.test.jsx
 *     included, where one test NAME carries the glyph), and ReportGroupedGrid
 *     fuses a superscript level index into the same string for multi-level
 *     sorts. Restyle the span; do not replace what is inside it.
 *   - numeric-aware compare (currency strings like "$1,200" compare as numbers)
 *   - stable tie-breaking: equal values keep their original relative order
 *
 * columns: [{ key, label, render?(row), sortValue?(row), align? ('right') }]
 * rows:    array of plain objects; row identity via rowKey(row) (default row.id).
 */

// Coerce a cell to something comparable. Numbers (or numeric-looking strings,
// currency/percent included) compare numerically; everything else as a string.
const toComparable = (v) => {
  if (v == null || v === '') return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'boolean') return v ? 1 : 0
  const s = String(v).trim()
  if (s === '') return null
  const looksNumeric = /\d/.test(s) && /^[($\-+]?[\d$,.\s%()]+$/.test(s)
  if (!looksNumeric) return s
  const n = Number(s.replace(/[$,%\s]/g, '').replace(/^\((.*)\)$/, '-$1'))
  return Number.isFinite(n) ? n : s
}

const isBlank = (v) => toComparable(v) == null

// Ascending compare for non-blank cells.
export const compareCells = (a, b) => {
  const ca = toComparable(a)
  const cb = toComparable(b)
  if (ca == null && cb == null) return 0
  if (ca == null) return 1
  if (cb == null) return -1
  if (typeof ca === 'number' && typeof cb === 'number') return ca === cb ? 0 : ca < cb ? -1 : 1
  return String(ca).localeCompare(String(cb), undefined, { numeric: true, sensitivity: 'base' })
}

const TileTable = ({ columns = [], rows = [], label, rowKey = (r) => r.id, defaultSort = null }) => {
  // null | { key, desc }. defaultSort seeds an initial sort (e.g. activity tiles
  // sorted by due date / time on open) — the user can still re-sort or clear it.
  const [sort, setSort] = useState(defaultSort)

  const toggleSort = (key) =>
    setSort((prev) => {
      // Ascending first for every column type (CC-49378), then desc, then off.
      if (!prev || prev.key !== key) return { key, desc: false }
      if (!prev.desc) return { key, desc: true }
      return null
    })

  const sorted = useMemo(() => {
    if (!sort) return rows
    const col = columns.find((c) => c.key === sort.key)
    const valueOf = (row) => (col?.sortValue ? col.sortValue(row) : row?.[sort.key])
    // Decorate with the original index so ties keep their incoming order
    // (stable tie-breaking, CC-49333) even where Array.sort isn't stable.
    return rows
      .map((row, i) => [row, i])
      .sort(([ra, ia], [rb, ib]) => {
        const va = valueOf(ra)
        const vb = valueOf(rb)
        // Blanks sink to the end in BOTH directions (outside the desc negation).
        const ba = isBlank(va)
        const bb = isBlank(vb)
        if (ba || bb) return ba && bb ? ia - ib : ba ? 1 : -1
        const r = compareCells(va, vb)
        return (sort.desc ? -r : r) || ia - ib
      })
      .map(([row]) => row)
  }, [rows, sort, columns])

  return (
    <table className="w-full text-sm" aria-label={label}>
      <thead>
        <tr className="border-b border-gray-100">
          {columns.map((col) => {
            const active = sort?.key === col.key
            return (
              <th
                key={col.key}
                scope="col"
                aria-sort={active ? (sort.desc ? 'descending' : 'ascending') : undefined}
                className={`py-1.5 pr-2 text-xs font-semibold text-gray-500 select-none ${col.align === 'right' ? 'text-right' : 'text-left'}`}
              >
                <button
                  type="button"
                  onClick={() => toggleSort(col.key)}
                  aria-label={`Sort by ${col.label}`}
                  className={`cursor-pointer select-none bg-transparent border-none p-0 inline-flex items-center gap-1 font-semibold text-xs text-gray-500 hover:text-gray-700 ${col.align === 'right' ? 'justify-end' : ''}`}
                >
                  {col.label}
                  {active && !sort.desc && <span className="text-rc-muted text-[10px] leading-none">▲</span>}
                  {active && sort.desc && <span className="text-rc-muted text-[10px] leading-none">▼</span>}
                </button>
              </th>
            )
          })}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {sorted.map((row, i) => (
          <tr key={rowKey(row) ?? i}>
            {columns.map((col) => (
              <td
                key={col.key}
                className={`py-2 pr-2 align-top text-gray-800 ${col.align === 'right' ? 'text-right tabular-nums' : ''} ${col.cellClass || ''}`}
              >
                {col.render ? col.render(row) : row?.[col.key] ?? '—'}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default TileTable
